import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Image as ImageIcon, Loader2, Star } from "lucide-react";

interface ImageData {
  id: string;
  image_url: string;
  alt_text: string;
  display_order: number;
  is_primary: boolean;
  created_at: string;
}

interface ImageGalleryProps {
  chateauId: string;
  onImageDeleted?: () => void;
}

export const ImageGallery = ({ chateauId, onImageDeleted }: ImageGalleryProps) => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingImages, setDeletingImages] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  console.log('🖼️ ImageGallery rendu pour château:', chateauId);
  
  const loadImages = async () => {
    try {
      console.log('🔍 Chargement images pour château:', chateauId);
      setIsLoading(true);
      const { data, error } = await supabase
        .from('chateau_images')
        .select('*')
        .eq('chateau_id', chateauId)
        .order('display_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) {
        throw error;
      }

      setImages(data || []);
    } catch (error: any) {
      console.error('Erreur lors du chargement des images:', error);
      toast({
        variant: "destructive",
        title: "Erreur de chargement",
        description: "Impossible de charger les images du château"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (chateauId) {
      loadImages();
    }
  }, [chateauId]);

  const handleSetPrimary = async (imageId: string) => {
    try {
      // 1. Retirer le statut primaire de toutes les images de ce château
      const { error: resetError } = await supabase
        .from('chateau_images')
        .update({ is_primary: false })
        .eq('chateau_id', chateauId);

      if (resetError) throw resetError;

      // 2. Définir cette image comme primaire
      const { error: setPrimaryError } = await supabase
        .from('chateau_images')
        .update({ is_primary: true })
        .eq('id', imageId);

      if (setPrimaryError) throw setPrimaryError;

      // 3. Mettre à jour l'état local
      setImages(prev => prev.map(img => ({
        ...img,
        is_primary: img.id === imageId
      })));

      toast({
        title: "Image principale définie",
        description: "Cette image sera affichée en premier sur la fiche du château"
      });

    } catch (error: any) {
      console.error('Erreur lors de la définition de l\'image principale:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de définir cette image comme principale"
      });
    }
  };

  const handleDeleteImage = async (image: ImageData) => {
    console.log('🗑️ Début suppression image:', image);
    
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer cette image ?`)) {
      return;
    }

    setDeletingImages(prev => new Set(prev).add(image.id));

    try {
      // 1. Supprimer l'enregistrement de la base de données
      console.log('🗑️ Suppression DB pour ID:', image.id);
      const { error: dbError } = await supabase
        .from('chateau_images')
        .delete()
        .eq('id', image.id);

      if (dbError) {
        console.error('❌ Erreur DB:', dbError);
        throw dbError;
      }
      console.log('✅ Suppression DB réussie');

      // 2. Extraire le nom du fichier de l'URL Supabase
      // Format URL Supabase: https://[project].supabase.co/storage/v1/object/public/bucket/path
      const url = new URL(image.image_url);
      const pathParts = url.pathname.split('/');
      // Trouver l'index après "public" et le nom du bucket
      const publicIndex = pathParts.indexOf('public');
      const bucketIndex = pathParts.indexOf('chateau-images');
      
      let filePath: string;
      if (bucketIndex > -1 && bucketIndex < pathParts.length - 1) {
        // Prendre tout après le nom du bucket
        filePath = pathParts.slice(bucketIndex + 1).join('/');
      } else {
        // Fallback: prendre les 2 derniers segments (chateau/filename)
        filePath = pathParts.slice(-2).join('/');
      }
      
      console.log('🗑️ URL complète:', image.image_url);
      console.log('🗑️ Chemin calculé:', filePath);

      // 3. Supprimer le fichier du storage
      const { error: storageError } = await supabase.storage
        .from('chateau-images')
        .remove([filePath]);

      if (storageError) {
        console.error('⚠️ Erreur storage (non bloquante):', storageError);
        // Ne pas bloquer si la suppression du fichier échoue
      } else {
        console.log('✅ Suppression storage réussie');
      }

      // 4. Mettre à jour la liste locale
      setImages(prev => prev.filter(img => img.id !== image.id));
      console.log('✅ Liste locale mise à jour');

      toast({
        title: "Image supprimée",
        description: "L'image a été supprimée avec succès"
      });

      onImageDeleted?.();

    } catch (error: any) {
      console.error('❌ Erreur lors de la suppression:', error);
      toast({
        variant: "destructive",
        title: "Erreur de suppression",
        description: error.message || "Impossible de supprimer l'image"
      });
    } finally {
      setDeletingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(image.id);
        return newSet;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Chargement des images...</span>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="text-center p-8 text-muted-foreground">
        <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Aucune image trouvée pour ce château</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ImageIcon className="w-5 h-5" />
        <h3 className="font-semibold">Images du château ({images.length})</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((image) => (
          <Card key={image.id} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="relative group">
                <img
                  src={image.image_url}
                  alt={image.alt_text || "Image du château"}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button
                    variant={image.is_primary ? "default" : "secondary"}
                    size="sm"
                    onClick={() => handleSetPrimary(image.id)}
                    className={image.is_primary ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                  >
                    <Star className={`w-4 h-4 ${image.is_primary ? 'fill-current' : ''}`} />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteImage(image)}
                    disabled={deletingImages.has(image.id)}
                  >
                    {deletingImages.has(image.id) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                {/* Badge étoile pour l'image principale */}
                {image.is_primary && (
                  <div className="absolute top-2 left-2">
                    <div className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center">
                      <Star className="w-3 h-3 fill-current mr-1" />
                      Principal
                    </div>
                  </div>
                )}
              </div>
              {image.alt_text && (
                <div className="p-2">
                  <p className="text-sm text-muted-foreground truncate">
                    {image.alt_text}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};